import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Shield, Compass, Sparkles, Scroll } from "lucide-react";

const HERO = "https://static.prod-images.emergentagent.com/jobs/9d7a7a96-cfaa-4032-bbb9-c78d2d79db34/images/ea1017d2770331745e9c5cb1af77f85f34eb81de6c72cb16545a365a011f5b98.png";

export default function Landing() {
  return (
    <div className="relative min-h-screen overflow-hidden" data-testid="landing-page">
      {/* Hero background */}
      <div className="absolute inset-0 -z-10">
        <img src={HERO} alt="Epic landscape" className="h-full w-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#070A11]/60 via-[#070A11]/85 to-[#070A11]" />
      </div>

      {/* Nav */}
      <header className="flex items-center justify-between px-8 py-6 md:px-16">
        <div className="flex items-center gap-2">
          <Shield className="h-6 w-6 text-[#D4AF37]" />
          <span className="font-serif-display text-2xl tracking-tight text-white">RPG<span className="text-[#D4AF37]">me</span></span>
        </div>
        <nav className="flex items-center gap-3">
          <Link to="/login">
            <Button variant="ghost" className="text-white hover:text-[#D4AF37]" data-testid="nav-login-btn">Sign in</Button>
          </Link>
          <Link to="/register">
            <Button className="bg-[#D4AF37] text-[#0b0f18] hover:bg-[#e7c75a]" data-testid="nav-signup-btn">
              Start your sheet
            </Button>
          </Link>
        </nav>
      </header>

      {/* Hero */}
      <section className="px-8 pb-16 pt-12 md:px-16 md:pt-28">
        <p className="font-mono-tight text-[11px] uppercase tracking-[0.3em] text-[#D4AF37]">It's your life. Be the main character.</p>
        <h1 className="font-serif-display mt-6 max-w-3xl text-5xl font-light leading-[1.05] text-white sm:text-6xl lg:text-7xl">
          Who you are. Who you can be. <em className="not-italic text-[#D4AF37]">Who you want to be.</em>
        </h1>
        <p className="mt-6 max-w-xl text-base leading-relaxed text-[#A0ABC0]">
          RPGme is a character sheet for the person you're becoming. Map your stats, name your skills,
          carry the things that matter, write the destiny in your own hand. Build yourself — and yes,
          when you want to, build anyone else you can imagine, too.
        </p>
        <div className="mt-10 flex flex-wrap gap-3">
          <Link to="/register">
            <Button size="lg" className="bg-[#D4AF37] px-8 text-[#0b0f18] hover:bg-[#e7c75a]" data-testid="hero-start-btn">
              Start my sheet
            </Button>
          </Link>
          <Link to="/login">
            <Button size="lg" variant="outline" className="border-white/20 bg-transparent text-white hover:bg-white/5" data-testid="hero-login-btn">
              I already have one
            </Button>
          </Link>
        </div>
      </section>

      {/* Features */}
      <section className="border-t border-[#212A3E]/60 bg-[#070A11]/80 px-8 py-20 md:px-16">
        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-10 md:grid-cols-3">
          {[
            { icon: Compass, title: "Stats are yours to name", text: "Strong, Nimble, Kind, Curious — rename anything. Add the skills that actually matter to you. Take some away. It's your sheet." },
            { icon: Scroll, title: "Versions of you", text: "Save snapshots: who you were, who you are, who you're aiming at. Organize them in folders. Pick the one you open by default." },
            { icon: Sparkles, title: "Your face, your hero", text: "Choose a silhouette, paste a portrait URL, or upload your own image. Build yourself — or any character you can dream up." },
          ].map(({ icon: Icon, title, text }) => (
            <div key={title} className="rounded-lg border border-[#212A3E] bg-[#101623] p-6 transition-colors hover:border-[#D4AF37]/60">
              <Icon className="h-6 w-6 text-[#D4AF37]" />
              <h3 className="font-serif-display mt-4 text-2xl text-white">{title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-[#A0ABC0]">{text}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="px-8 py-8 text-center text-xs text-[#A0ABC0] md:px-16">
        Made for the people writing their own story. — RPGme
      </footer>
    </div>
  );
}
